/**
 *  @swagger
 *  definitions:
 *    AdminAccessTokenParams:
 *      type: object
 *      properties:
 *        AdminId:
 *          description: ID of the associated Admin
 *          type: string
 *        Name:
 *          description: Name of the AdminAccessToken
 *          type: string
 *        Exp:
 *          description: Expire date of the AdminAccessToken
 *          type: string
 *          format: date-time
 *          nullable: true
 *        Status:
 *          description: Status of the AdminAccessToken
 *          $ref: '#/components/enums/AdminAccessTokenStatus'
 *        Agent:
 *          description: Agent of the AdminAccessToken
 *          type: object
 *          properties:
 *            platform:
 *              type: string
 *            os:
 *              type: string
 *            browser:
 *              type: string
 *            ip:
 *              type: string
 *            country:
 *              type: string
 *            city:
 *              type: string
 *        AuthType:
 *          description: Auth Type of the AdminAccessToken
 *          $ref: '#/components/enums/AdminAccessTokenAuthType'
 *
 *  @swagger
 *  definitions:
 *    AdminAccessTokenCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminAccessTokenParams'
 *        - type: object
 *          required:
 *            - AdminId
 *            - Name
 *            - Exp
 *            - Status
 *            - Agent
 *            - AuthType
 *
 *  @swagger
 *  definitions:
 *    AdminAccessTokenQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the AdminAccessToken
 *              type: string
 *        - $ref: '#/definitions/AdminAccessTokenParams'
 *
 *  @swagger
 *  definitions:
 *    AdminAccessTokenUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/AdminAccessTokenQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    AdminAccessToken:
 *      allOf:
 *        - $ref: '#/definitions/AdminAccessTokenCreateParams'
 *        - $ref: '#/definitions/AdminAccessTokenUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      AdminAccessToken:
 *        allOf:
 *          - $ref: '#/definitions/AdminAccessToken'
 *          - type: object
 *            properties:
 *              Admin:
 *                description: Admin of the AdminAccessToken
 *                $ref: '#/components/schemas/Admin'
 */

// Enums
import {
  AdminAccessTokenAuthType,
  AdminAccessTokenStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Agent,
  $Field,
} from '../types';

export type $RelationIds = {
  AdminId: string;
};

export type $Params = {
  Agent: $Agent;
  AuthType: AdminAccessTokenAuthType;
  Exp: Date;
  Name: string;
  Status: AdminAccessTokenStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admin?: $Model<'Admin'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Agent: {
    allowNull: false,
    type: Sequelize.JSON,
    validate: {
      notEmpty: true,
    },
  },
  AuthType: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(AdminAccessTokenAuthType)),
    validate: {
      notEmpty: true,
    },
  },
  Exp: {
    allowNull: false,
    type: Sequelize.DATE,
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(AdminAccessTokenStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'AdminAccessToken'> => {
  const AdminAccessToken = stack.define(
    'AdminAccessToken',
    fields(Sequelize),
    {
      tableName: 'AdminAccessTokens',
    },
  );

  AdminAccessToken.associate = (models: $ModelClasses) => {
    models.AdminAccessToken.belongsTo(
      models.Admin,
      {
        as: 'Admin',
        foreignKey: 'AdminId',
      },
    );
  };

  AdminAccessToken.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return AdminAccessToken;
});
